/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import * as studioConstants from '../../constants/StudioTextConstants';

/**
 * Consume run-graph SSE response body and concatenate plain text chunks.
 * Matches GraphTester / prompt-generator event shape: STREAMING + CHUNK without contentType.
 *
 * @param {Response} response - fetch Response from runGraphService
 * @param {string} fallbackErrorMessage - thrown when body missing or no text accumulated
 * @param {{ allowEmpty?: boolean, onEvent?: (payload: object, textSoFar: string) => void }} [options]
 *   onEvent (optional) is called for every parsed event, after its text is added, so callers can show
 *   the answer and progress while the stream is still arriving.
 * @returns {Promise<string>}
 */
async function parseRunGraphStreamToText(response, fallbackErrorMessage, options = {}) {
  const { allowEmpty = false, onEvent } = options;

  if (!response?.body) {
    throw new Error(fallbackErrorMessage);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let pending = '';
  let streamText = '';

  const consumeRawEvent = (rawEvent) => {
    const trimmed = rawEvent.trim();
    if (!trimmed) return;

    let chunkPayload;
    try {
      const jsonLine = trimmed.startsWith('data: ') ? trimmed.slice(6) : trimmed;
      chunkPayload = JSON.parse(jsonLine);
    } catch {
      return;
    }

    const { streamStatus, type, content, contentType } = chunkPayload;
    const isStreamingChunk = streamStatus === studioConstants.STREAMING && type === studioConstants.CHUNK;
    if (isStreamingChunk && !contentType) {
      streamText += String(content ?? '');
    }
    onEvent?.(chunkPayload, streamText);
  };

  const readNextChunk = async () => {
    const { done, value } = await reader.read();
    if (done) return;

    pending += decoder.decode(value, { stream: true });
    const events = pending.split('\n\n');
    pending = events.pop() || '';
    events.forEach(consumeRawEvent);
    await readNextChunk();
  };

  await readNextChunk();

  pending += decoder.decode();
  if (pending.trim()) {
    pending.split('\n\n').forEach(consumeRawEvent);
  }

  if (!allowEmpty && !streamText.trim()) {
    throw new Error(fallbackErrorMessage);
  }

  return streamText;
}

export default parseRunGraphStreamToText;
